import React, { useState, useEffect, FocusEvent, ChangeEvent } from 'react';
import { Form, Icon, Popup } from 'semantic-ui-react';
import numbro from 'numbro';
import FieldToolTip from './FieldToolTip';
import Outlier from './Outlier';

interface NumberComponentProps {
    name?: string;
    label?: string;
    value?: string | number;
    description?: string;
    onChange?: (
        event: ChangeEvent<HTMLInputElement>,
        data: {
            name: string;
            value: string | number | undefined;
            outlier?: boolean;
            excludeFromStatistics?: boolean;
        },
    ) => void;
    regex?: RegExp;
    disabled?: boolean;
    errors?: object;
    required?: boolean;
    editable?: boolean;
    error?: boolean;
    onFocus?(name: string | undefined): string;
    onBlur?(name: string | undefined): string;
    alwaysReturnString?: boolean;
    outlier?: boolean;
    excludeFromStatistics?: boolean;
    dataField?: boolean;
    isManufacturer?: boolean;
    descriptionOnScreen?: boolean;
}

const NumberComponent = ({
    name,
    label,
    value: propValue,
    onChange,
    regex,
    disabled = false,
    description,
    errors,
    required,
    editable = true,
    error,
    onFocus,
    onBlur,
    alwaysReturnString,
    dataField,
    excludeFromStatistics,
    outlier,
    isManufacturer,
    descriptionOnScreen,
}: NumberComponentProps) => {
    const [numberValue, setNumberValue] = useState<number | null>(null);
    const [stringValue, setStringValue] = useState<string>('');
    const [editingStringValue, setEditingStringValue] = useState<string>('');

    const [isFocused, setIsFocused] = useState<boolean>(false);

    useEffect(() => {
        if (propValue !== undefined && propValue !== null) {
            const parsedValue = parseFloat(propValue.toString());
            setNumberValue(isNaN(parsedValue) ? null : parsedValue);
            setEditingStringValue(
                isNaN(parsedValue) ? '' : parsedValue.toString(),
            );
            setStringValue(formatNumber(propValue));
        }
    }, [propValue]);

    const formatNumber = (value: string | number | null): string => {
        if (value === null || value === '') {
            return '';
        }
        return numbro(value).format({ thousandSeparated: true });
    };

    const getDisplayedValue = () => {
        if (isFocused) {
            return editingStringValue;
        }
        return stringValue;
    };

    const displayedValue = getDisplayedValue();

    const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
        setIsFocused(true);
        setStringValue(numberValue !== null ? numberValue.toString() : '');
        onFocus?.(name);
    };

    const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
        setIsFocused(false);
        onBlur?.(name);
    };

    const handleInputChanged = (
        event: ChangeEvent<HTMLInputElement>,
        { name, value }: { name?: any; value: string },
    ) => {
        if (value.length < 17) {
            const validRegex = regex ? regex : /^[0-9.]+$/;
            if (value === '' || validRegex.test(value)) {
                const parsedValue =
                    value === '' ? undefined : parseFloat(value);
                onChange?.(event, {
                    name,
                    value: alwaysReturnString ? value : parsedValue,
                });
                // Casts only: isNaN(undefined) is NaN-ish (true) at runtime,
                // so the null branch is taken exactly as before.
                setNumberValue(
                    isNaN(parsedValue as number)
                        ? null
                        : (parsedValue as number),
                );
                setStringValue(value);
                setEditingStringValue(value);
            }
        }
    };

    if (!editable) {
        return <>{displayedValue}</>;
    }

    return (
        <Form.Field required={required} error={error}>
            <label>
                {label}
                {!descriptionOnScreen && (
                    <FieldToolTip description={description} />
                )}
                {isManufacturer && dataField && (
                    <Outlier
                        value={propValue}
                        name={name}
                        outlier={outlier}
                        excludeFromStatistics={excludeFromStatistics}
                        onChange={onChange}
                    />
                )}
            </label>
            {descriptionOnScreen && description && (
                <div style={{ whiteSpace: 'pre-line' }}>{description}</div>
            )}
            <Form.Input
                type="text"
                placeholder={label}
                value={displayedValue}
                name={name}
                onChange={handleInputChanged}
                onBlur={handleBlur}
                onFocus={(e: FocusEvent<HTMLInputElement>) => {
                    e.target.addEventListener(
                        'wheel',
                        function (e: WheelEvent) {
                            e.preventDefault();
                        },
                        { passive: false },
                    );
                    handleFocus(e);
                }}
                disabled={disabled}
            />
        </Form.Field>
    );
};

export default NumberComponent;
